import React, { useState, useEffect } from "react";
import AthleteLayout from "../common/AthleteLayout";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import { Link, useParams } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import API_URL from "../../api/API";
import ladyRun from "../../images/ladyRun.png";

export const AthleteStaticSpeedTemplateDetail = () => {
  const { athlete, dispatch } = useAthleteAuthContext();
  const { id } = useParams();

  const stripe = useStripe();
  const elements = useElements();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isPurchased, setIsPurchased] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        const response = await fetch(
          `${API_URL.FRONTEND_SPEED_PRODUCT_DETAIL}${id}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${athlete.token}` },
          }
        );

        if (!response.ok)
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        const data = await response.json();
        setProduct(data.data);

        //console.log(data.data);

        //console.log(athlete);

        // Fetch athlete's purchase data
        const purchaseResponse = await fetch(
          `${API_URL.GET_ATHLETE_PURCHASE_DATA}${athlete.id}`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${athlete.token}` },
          }
        );

        if (purchaseResponse.ok) {
          const purchaseData = await purchaseResponse.json();
          console.log("Purchase data:", purchaseData);

          // Robust check
          const purchased = purchaseData.data?.speed?.some(
            (item) =>
              String(item.template_id) === String(id) &&
              item.payment_status?.toUpperCase() === "COMPLETED"
          );

          if (purchased) setIsPurchased(true);
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching product detail:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetail();
  }, [id]);

  // Step 1: Create PaymentIntent on backend
  const handleBuyClick = async () => {
    setShowPaymentForm(true);
  };

  // Step 2: Confirm payment
  const handlePayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    if (!stripe || !elements) return;

    try {
      // Create PaymentIntent on backend
      const intentResponse = await fetch(API_URL.CREATE_PAYMENT_INTENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${athlete.token}`,
        },
        body: JSON.stringify({
          amount: Math.round(product.price),
          currency: "usd",
          athlete_id: `${athlete.firstname} | ${athlete.email}`,
          template_id: product.title,
          category: "speed",
        }),
      });

      const { clientSecret } = await intentResponse.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) {
        setError(result.error.message);
      } else if (result.paymentIntent.status === "succeeded") {
        await fetch(API_URL.POST_ATHLETE_PURCHASE_SPEED_DATA + athlete.id, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${athlete.token}`,
          },
          body: JSON.stringify({
            template_id: product._id,
            warmup_routine_id: product.warmup_template_id,
            price: product.price,
            payment_transaction_id: result.paymentIntent.id,
            payment_logs: "Stripe inline payment success",
            payment_status: "COMPLETED",
          }),
        });

        setSuccessMessage("Payment successful! You now have access.");
        setIsPurchased(true);
        setShowPaymentForm(false);

        // Update athlete context
        // Update athlete account type ONLY if not AMAX COACHED
        if (athlete.account_type !== "AMAX COACHED") {
          dispatch({ type: "UPDATE_ACCOUNT_TYPE", payload: "STATIC PLAN" });
          localStorage.setItem(
            "athlete",
            JSON.stringify({ ...athlete, account_type: "STATIC PLAN" })
          );
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePartnerAccess = async () => {
    try {
      await fetch(
        API_URL.POST_ATHLETE_PURCHASE_SPEED_DATA + athlete.id,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${athlete.token}`,
          },
          body: JSON.stringify({
            template_id: product._id,
            warmup_routine_id: product.warmup_template_id,
            price: product.price || 0,
            payment_transaction_id: "PARTNER_PURCHASE",
            payment_logs: "Partner purchase confirmed by athlete",
            payment_status: "COMPLETED",
          }),
        }
      );
  
      setIsPurchased(true);
  
      if (athlete.account_type !== "AMAX COACHED") {
        dispatch({
          type: "UPDATE_ACCOUNT_TYPE",
          payload: "STATIC PLAN",
        });
  
        localStorage.setItem(
          "athlete",
          JSON.stringify({
            ...athlete,
            account_type: "STATIC PLAN",
          })
        );
      }
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <AthleteLayout>
      <div className="bg-white h-full overflow-y-auto">
        {loading ? (
          <div className="text-center p-10">Loading product details...</div>
        ) : error ? (
          <div className="text-center text-red-500 p-10">Error: {error}</div>
        ) : (
          product && (
            <div className="p-5 w-full max-w-2xl mx-auto">
              {/* Product Display */}
              <div className="bg-smock rounded-2xl p-5 mx-auto relative shadow-sm">
                <div className="flex justify-center items-center">
                  {product.youtube_url ? (
                    <iframe
                      className="rounded-xl w-full aspect-video"
                      src={`https://www.youtube.com/embed/${extractYouTubeId(
                        product.youtube_url
                      )}`}
                      title="Product Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <img
                      src={
                        product.image
                          ? `${API_URL.SERVER_PICTURE_URL}${product.image}`
                          : ladyRun
                      }
                      alt={product.title}
                      className="object-cover w-full sm:h-80 rounded-xl"
                    />
                  )}
                </div>

                <Link
                  to="/athlete/purchase/static-speed-templates"
                  className="absolute top-3 left-3 bg-white size-8 p-2 rounded-full flex justify-center items-center hover:bg-gray-100 transition-all"
                >
                  <ArrowL />
                </Link>
              </div>

              {/* Product Details */}
              <div className="mt-5">
                {/* <div className="space-y-5"> */}
                  <div className="space-y-2">
                    <h2 className="text-black text-xl">{product.title}</h2>
                    <p className="text-black text-base font-light mt-1">
                      ${product.price}
                    </p>
                    <div className="bg-smock rounded-2xl p-5">
                      <p className="text-black text-base font-light leading-relaxed whitespace-pre-line">
                        {product.info || "No description available."}
                      </p>
                    </div>
                  </div>
                {/* </div> */}
              </div>

              {/* Success */}
              {successMessage && (
                <div className="bg-green-500 text-white p-4 rounded-md mt-4 text-center">
                  {successMessage}
                </div>
              )}

              {/* Inline Payment Form */}
    {/* Partner Product Purchase */}
{product.product_source === "PARTNER" &&
product.partner_purchase_url ? (
  <div className="mt-8">
    <a
      href={product.partner_purchase_url}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full p-6 bg-black text-white text-center text-base font-semibold rounded-[70px] hover:opacity-80 transition"
    >
      Purchase Through Our Partner
    </a>

    <p className="text-center text-sm text-gray-500 mt-3">
      Complete your purchase securely through our partner.
    </p>
    {!isPurchased ? (
  <button
    onClick={handlePartnerAccess}
    className="w-full p-4 border border-black rounded-[70px] text-base font-medium hover:bg-gray-100"
  >
    I Already Purchased This Product
  </button>
) : (
  <Link
    to="/athlete/speed-products"
    className="text-center w-full p-3 xs:p-4 text-white text-base leading-none bg-black hover:bg-black/70 rounded-xl transition-all duration-300 block mt-4"
  >
    Access Now
  </Link>
)}
  </div>
) : (
  <>
    {/* Stripe Payment Form */}
    {showPaymentForm ? (
      <form onSubmit={handlePayment} className="mt-6 space-y-4">
        <CardElement className="p-4 border rounded-md bg-white" />

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <button
          type="submit"
          disabled={!stripe || saving}
          className="p-4 bg-black text-white rounded-full w-full disabled:opacity-50"
        >
          {saving ? "Processing..." : "Pay Now"}
        </button>
      </form>
    ) : (
      !isPurchased && (
        <button
          onClick={handleBuyClick}
          className="p-6 border border-black text-base font-semibold hover:opacity-70 rounded-[70px] w-full mt-8"
        >
          Buy Now
        </button>
      )
    )}

    {/* Access Button */}
    {isPurchased && (
      <Link
        to={`/athlete/speed-products`}
        className="text-center w-full p-3 xs:p-4 text-white text-base leading-none bg-black hover:bg-black/70 rounded-xl transition-all duration-300 block mt-6"
      >
        Access Now
      </Link>
    )}
  </>
)}
            </div>
          )
        )}
      </div>
    </AthleteLayout>
  );
};

// Helpers
const extractYouTubeId = (url) => {
  try {
    const match = url.match(
      /(?:youtube\.com\/.*v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : null;
  } catch {
    return null;
  }
};

const ArrowL = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path
      d="M7 13L1 7L7 1"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

{
  /*
   */
}
